---
title: "Schluss mit dem \"Transformations-Theater\": Warum 2026 das Jahr der Stabilität werden muss"
date: "2026-02-11"
excerpt: "Die Januar-Ausgabe des Harvard Business Review läutet einen Paradigmenwechsel ein: 'Get Off the Transformation Treadmill'. Führende Strategen wie Darrell Rigby warnen, dass der ständige Reorganisations-Zyklus Organisationen nicht agiler, sondern fragiler macht. Die Lösung liegt nicht im nächsten großen 'Big Bang', sondern im Wechsel zur 'Project-Driven Organization'."
metaTitle: "HBR Jan 2026: Warum ständige Transformationen scheitern"
metaDescription: "Neue HBR-Studie (Jan/Feb 2026): 'Get Off the Transformation Treadmill'. Warum Unternehmen aufhören müssen, sich ständig neu zu erfinden, und wie 'Continuous Adaptation' stattdessen funktioniert."
language: "de"
slug: "ende-der-dauer-transformation-hbr-2026"
author: "Kevin Rassner"
authorUrl: "https://rassner-dev.org"
canonicalUrl: "https://rassner-dev.org/blog/ende-der-dauer-transformation-hbr-2026"
publishedOn: "rassner-dev.org"
tags: ["Organisationsdesign","Change Management","Strategie","HBR"]

---

Die Frage, die sich 2025 viele Führungskräfte stellten, lautete oft: **Wann starten wir die nächste Transformation?**
Für 2026 schlagen die aktuellen Autoren des Harvard Business Review eine andere Frage vor: **„Warum hören wir nicht endlich damit auf?“**

Die Januar-Ausgabe des Harvard Business Review gleicht einem Weckruf. In [Get Off the Transformation Treadmill](https://hbr.org/2026/01/get-off-the-transformation-treadmill) analysieren Bain-Partner Darrell Rigby und Zach First, was viele in Strategie-Workshops vielleicht nur denken:

> Haben wir Agilität geschaffen, oder eine Kultur der ständigen Reorganisation, die Unternehmen nicht schneller, sondern müder macht?

## Die "Transformations-Tretmühle"

Haben Sie schon einmal eine Reorganisation erlebt, die nicht primär dazu diente, von schlechten Quartalszahlen abzulenken?
Rigby und First zeigen auf, dass "Transformation" zu einem reflexartigen Ritual verkommen ist. Wenn die Zahlen nicht stimmen, wird das Organigramm umgebaut.

Das Problem dabei ist nicht der Wandel an sich, sondern die Art und Weise:
1.  **Change Fatigue**: Mitarbeitende resignieren vor der nächsten "Bold New Strategy", weil sie wissen, dass sie in 18 Monaten ohnehin wieder verworfen wird.
2.  **Interne Nabelschau**: Während wir Kästchen verschieben und Titel neu vergeben, verliert das Unternehmen den Fokus auf das Einzige, was zählt: den Kunden.
3.  **Die Illusion von Kontrolle**: Eine neue Struktur suggeriert Handlungsfähigkeit, verdeckt aber oft nur tiefere Prozess-Probleme.

## Von "Kästchen schieben" zu echter Arbeit

Wenn wir aufhören, Organigramme zu malen, was tun wir dann? Die Antwort liefert Antonio Nieto-Rodriguez in [The Project-Driven Organization](https://hbr.org/2026/01/the-project-driven-organization).

Sein Argument ist einfach: **Wertschöpfung passiert heute in Projekten, nicht in Abteilungen.**
Doch unsere Firmen sind immer noch auf starre Funktionen (Marketing, Sales, IT) optimiert. Das Ergebnis? Projekte sind "Schattenarbeit", die *neben* dem eigentlichen Job erledigt werden müssen.

### Das Modell 2026: Structure Follows Project
In einer "Project-Driven Organization" ist das Projekt keine Zusatzaufgabe – es ist der Job.
*   **Keine festen Silos**: Talente werden für die Laufzeit eines strategischen Projekts zusammengezogen.
*   **Fokus statt Multitasking**: Mitarbeitende arbeiten an *einem* großen Projekt, nicht an fünf kleinen neben dem Tagesgeschäft.
*   **Run-the-Business vs. Change-the-Business**: Die operative Routine ("Run") wird zunehmend automatisiert (KI lässt grüßen), damit Menschen sich auf die Veränderung ("Change") konzentrieren können.

## McKinsey's Pivot: "Outcome ist die neue Strategie"

Vielleicht das überraschendste Signal kommt von McKinsey selbst. Im Interview [We Want to Make Ourselves Better](https://hbr.org/2026/01/we-want-to-make-ourselves-better) erklärt Global Managing Partner Bob Sternfels einen fundamentalen Shift im Beratungsgeschäft.

Weg vom **"Strategy Paper"** (wir sagen euch, was ihr tun sollt) hin zum **"Underwriting Outcomes"** (wir werden daran gemessen, ob es funktioniert).
Für Organisationsentwickler heißt das: Die Zeit der theoretischen Target-Operating-Models (TOMs) auf Powerpoint ist vorbei. Eine Struktur ist nur so gut wie die Ergebnisse, die sie *messbar* liefert.

## 3 Disziplinen für 2026

Wenn Sie Verantwortung für Organisation oder Strategie tragen, bieten die Autoren folgende Impulse für dieses Jahr:

1.  **Hinterfragen Sie die nächste Reorganisation**: Wenn jemand vorschlägt, die Struktur zu ändern, fragen Sie: "Welches Kundenproblem lösen wir damit?"
2.  **Professionalisieren Sie Projektarbeit**: Projektmanagement wird als die Kernkompetenz der Zukunft beschrieben. Der Aufruf ist klar: Investieren Sie in echte Projekt-Skills, nicht nur in neue Titel.
3.  **Schaffen Sie "Inseln der Stabilität"**: In einer volatilen Welt kann Beständigkeit zum Wettbewerbsvorteil werden. Geben Sie Ihren Teams die Sicherheit, dass ihre Struktur Bestand hat – damit sie den Kopf frei haben für echte Innovation.

**Fazit:**

Vielleicht ist dieser Fokus auf Projekte nur die "DOGEisierung" der Wirtschaft? Weg mit dem administrativen Überbau, am besten mit der Kettensäge.
Denn wer kann schon einer Führungskraft widersprechen, die sich radikal auf Wertbeitrag fokussieren will? Der Vorstand und die Aktionäre schon mal nicht. 

Ich bin gespannt, was ihr denkt!
